import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import { type DocTypeConfig, regenerateSection } from "../../api/client";
import type { RegenConfirmation, RegenTarget } from "../components/RegenOne";
import { estMinutes, sizeFromWordTarget } from "../selection";
import type { SpineSectionStatus } from "../sseAdapter";
import type { VariantCEvent } from "../state";
import type { SpineSection } from "./useVariantCRun";

export interface UseRegenFlowParams {
  focusedSectionId: string | null;
  docConfig: DocTypeConfig | null;
  sections: readonly SpineSection[];
  runId: string | null;
  runState: string;
  dispatch: Dispatch<VariantCEvent>;
  resolveSectionLabel: (id: string | null) => string | null;
  /** Eagerly flip the focused section's spine row to ``drafting`` on
   *  confirm so the writer doesn't see a stale ``drafted`` row while the
   *  backend boots the regen. The real SSE ``regen_started`` /
   *  ``module_start`` events overwrite this within a tick. */
  setSectionStatus: (sectionId: string, status: SpineSectionStatus) => void;
  /** Clear the terminal-run SSE gate so ``useVariantCRun`` re-subscribes
   *  to the original run's stream. After a reload of a completed run,
   *  this is true and would otherwise short-circuit the subscribe effect
   *  before the regen's events could land. */
  setTerminalRunStatus: (v: boolean) => void;
  /** Bump the subscription nonce so the SSE effect re-runs even though
   *  ``runId`` hasn't changed (we're staying on the same run_id, by
   *  design). Paired with the ``terminalRunStatus`` reset above. */
  setRunSubscriptionNonce: Dispatch<SetStateAction<number>>;
  /** Close the Inspector drawer when regeneration is initiated. */
  setFocusedSectionId: Dispatch<SetStateAction<string | null>>;
}

export interface UseRegenFlowResult {
  regenTarget: RegenTarget | null;
  regenEnabled: boolean;
  handleInspectorRegenerate: () => void;
  /** Open the same regenerate dialog for a given section — used by the
   *  composer's "Regenerate this section" chip (cursor section). */
  openRegenFor: (sectionId: string) => void;
  handleRegenConfirm: (confirmation: RegenConfirmation) => Promise<void>;
  handleRegenCancel: () => void;
}

export function useRegenFlow({
  focusedSectionId,
  docConfig,
  sections,
  runId,
  runState,
  dispatch,
  resolveSectionLabel,
  setSectionStatus,
  setTerminalRunStatus,
  setRunSubscriptionNonce,
  setFocusedSectionId,
}: UseRegenFlowParams): UseRegenFlowResult {
  const [regenTarget, setRegenTarget] = useState<RegenTarget | null>(null);

  // "Regen" is only meaningful once a section has a draft to
  // regenerate; we gate the affordance on post-run state (the
  // drafted column is final). Pre-run / mid-run surfaces hide it.
  const regenEnabled = runState === "post-run";

  // The inspector "Regen" button opens a modal; writer confirms with
  // optional guidance. On confirm we dispatch the ``regen`` EventLog
  // entry and kick off a fresh single-section run via the same
  // `actuallyStartRun` primitive `Draft more sections` uses.
  const openRegenFor = useCallback(
    (sectionId: string) => {
      if (!docConfig) return;
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;
      // Derive the section's gate from docConfig (SpineSection doesn't
      // carry gate itself — it lives in the doctype config).
      const moduleId = Object.entries(docConfig.modules ?? {}).find(([, mod]) =>
        (mod.sections ?? []).includes(section.id),
      )?.[0];
      const gateIndex = (docConfig.gates ?? []).findIndex((g) => moduleId && (g.modules ?? []).includes(moduleId));
      const gateEntry = gateIndex >= 0 ? (docConfig.gates ?? [])[gateIndex] : null;
      // Honest time estimate: same word-target → size → minutes chain
      // LoomSelection uses.
      const skelRow = (docConfig.skeleton ?? []).find(([id]) => id === section.id);
      const wordTarget = skelRow ? skelRow[2] : 0;
      const est = estMinutes({
        n: section.id,
        t: section.title,
        gate: gateEntry?.name ?? "",
        size: sizeFromWordTarget(wordTarget),
      });
      setRegenTarget({
        sectionId: section.id,
        sectionNumber: section.number,
        sectionTitle: section.title,
        gateName: gateEntry?.name ?? "",
        gateDisplayName: gateEntry?.display_name ?? "",
        gatePaletteIndex: Math.max(0, gateIndex),
        estMinutes: est,
      });
    },
    [docConfig, sections],
  );

  const handleInspectorRegenerate = useCallback(() => {
    if (focusedSectionId) openRegenFor(focusedSectionId);
  }, [focusedSectionId, openRegenFor]);

  const handleRegenConfirm = useCallback(
    async (confirmation: RegenConfirmation) => {
      // Append the writer's regen intent to the EXISTING run's
      // transcript. No RUN_STARTED — we're not starting a new run; the
      // backend resets the section's SSOT and re-dispatches the gate
      // runner on the same run_id, emitting events into the SAME Redis
      // stream the original draft used.
      dispatch({
        type: "EVENT_APPENDED",
        entry: {
          ts: Date.now(),
          actor: "you",
          sectionId: confirmation.sectionId,
          sectionLabel: resolveSectionLabel(confirmation.sectionId),
          runId,
          payload: {
            kind: "regen",
            attempt: 1,
            outcome: "submitted",
            guidance: confirmation.guidance,
          },
        },
      });
      try {
        if (!runId) {
          // Regen is gated on post-run (regenEnabled); a post-run state
          // implies a run_id exists. Defensive bail keeps the modal from
          // wedging if the gate ever drifts.
          if (typeof console !== "undefined") {
            console.warn(
              "[useRegenFlow] handleRegenConfirm with null runId — skipping regenerateSection",
            );
          }
          return;
        }
        // Eager paint: flip the section's spine row to ``drafting`` on
        // confirm so the writer gets instant feedback instead of staring
        // at a stale ``drafted`` row while the backend spins up. The SSE
        // ``regen_started`` / ``module_start`` events will overwrite
        // within a tick with live sublines.
        setSectionStatus(confirmation.sectionId, "drafting");
        // Clear the SSE-skip gate that bootstrap-rehydration sets for
        // terminal runs (avoids 404s on the prior, evicted run). For a
        // regen the Redis Stream is still alive and the runner is about
        // to write into it — we MUST re-subscribe to see the events.
        setTerminalRunStatus(false);
        // Force the SSE subscribe effect to re-run even though runId
        // hasn't changed (same run_id, by design).
        setRunSubscriptionNonce((n) => n + 1);
        await regenerateSection(runId, confirmation.sectionId, {
          guidance: confirmation.guidance,
          source: "inspector",
        });
        // Close the Inspector drawer once regeneration is initiated.
        setFocusedSectionId(null);
      } finally {
        setRegenTarget(null);
      }
    },
    [
      runId,
      resolveSectionLabel,
      dispatch,
      setSectionStatus,
      setTerminalRunStatus,
      setRunSubscriptionNonce,
      setFocusedSectionId,
    ],
  );

  const handleRegenCancel = useCallback(() => {
    setRegenTarget(null);
  }, []);

  return {
    regenTarget,
    regenEnabled,
    handleInspectorRegenerate,
    openRegenFor,
    handleRegenConfirm,
    handleRegenCancel,
  };
}
